import domains from '../config/portalDomains.json';

const previewPortals = new Map();

function currentHostname() {
    return typeof window !== 'undefined' ? window.location?.hostname : '';
}

/** A known hostname selects presentation only; account permissions stay on the API. */
export function portalForHostname(hostname = currentHostname()) {
    const host = String(hostname || '').toLowerCase();
    const registered = Object.values(domains).find((portal) => portal.hostname === host);
    if (registered) return registered;
    // An explicit preview build may exercise the same portal on Vercel's own
    // deployment hostname. Query parameters and custom hosts cannot select it.
    if (process.env.REACT_APP_VERCEL_ENV !== 'preview' || process.env.REACT_APP_PORTAL_PREVIEW !== 'tyson'
        || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.vercel\.app$/.test(host)) return null;
    const expectedHost = String(process.env.REACT_APP_PORTAL_PREVIEW_HOST || '').toLowerCase();
    if (expectedHost && expectedHost !== host) return null;
    if (!previewPortals.has(host)) {
        previewPortals.set(host, { ...domains.tyson, hostname: host, origin: `https://${host}` });
    }
    return previewPortals.get(host);
}

/** Keep the original branded paths available on IPM and other existing hosts. */
export function authPathsForExperience(experience, hostname = currentHostname()) {
    const portal = portalForHostname(hostname);
    if (portal) return { experience: portal.experience, loginPath: portal.loginPath, recoveryPath: portal.recoveryPath };
    if (experience === domains.tyson.experience) {
        return {
            experience,
            loginPath: domains.tyson.legacyLoginPath,
            recoveryPath: domains.tyson.legacyRecoveryPath,
        };
    }
    return { experience: 'ipm', loginPath: '/login', recoveryPath: '/forgot-password' };
}

/** Product identity is presentation only and never establishes account membership. */
export function productNameForHostname(fallback = 'IPM', hostname = currentHostname()) {
    return portalForHostname(hostname)?.productName || fallback;
}

/** Translate explicit static UI product copy, never stored records, legal parties or URLs. */
export function productLabelForHostname(label, hostname = currentHostname()) {
    const portal = portalForHostname(hostname);
    if (!portal || typeof label !== 'string') return label;
    return label
        .replace(/\bInternational Property Market\b/gi, portal.productName)
        .replace(/\bMyIPM\b/gi, `My ${portal.productName}`)
        .replace(/\bIPMSign\b/g, `${portal.productName} Sign`)
        .replace(/\bIPM\b/g, portal.productName);
}

const PRIVATE_OPERATOR_PAGES = [
    '/admin/ipm-sales', '/admin/sprints', '/admin/workbench', '/admin/email-health',
    '/admin/screening', '/admin/communication-billing', '/admin/scheduler',
    '/admin/ops', '/admin/runtime-identity', '/admin/cost-model', '/admin/demo', '/admin/demo-websites',
    '/admin/demo-accounts', '/admin/demo-builder', '/admin/template-portal',
];

/** Operator tooling is available at the existing platform host, outside this portal. */
export function isPortalOperatorPage(pathname, hostname = currentHostname()) {
    if (!portalForHostname(hostname)) return false;
    let path = String(pathname || '').split('?')[0];
    try { path = decodeURIComponent(path); } catch (_) { /* Router handles malformed paths. */ }
    path = path.toLowerCase().replace(/\/+$/, '');
    return PRIVATE_OPERATOR_PAGES.some(page => path === page || path.startsWith(`${page}/`));
}
